import { fetcher, SWRFetcher } from '@commenda/utils';
import useSWR from 'swr';
import { Setting, SettingEntity, SettingKey, SettingValueType } from './types';

/**
 * Non-hook variant of useSettingsList. Use this when you need to fetch
 * settings imperatively (e.g. inside Promise.all over multiple entity
 * tuples). Settings are scoped by entitiesHash, so each distinct entities
 * array is a distinct row — callers that iterate corporations should fetch
 * per-corp with [CORP, COMPANY] to match the write scope used by
 * updateSetting on the indirect-tax exposure page.
 */
export async function fetchSettingsList(
  entities: SettingEntity[]
): Promise<{ success: boolean; settings: Setting[]; error?: string }> {
  try {
    const response = await fetcher('settings/list', {
      method: 'POST',
      body: { entities },
    });
    const data = await response.json();
    if (!response.ok) {
      return {
        success: false,
        settings: [],
        error: data.message || 'Failed to load settings',
      };
    }
    return { success: true, settings: data.value ?? [] };
  } catch (error) {
    return {
      success: false,
      settings: [],
      error:
        error instanceof Error ? error.message : 'An unexpected error occurred',
    };
  }
}

export function useSettingsList(entities: SettingEntity[]) {
  const listKey = entities
    ? `settings/list?entities=${encodeURIComponent(JSON.stringify(entities))}`
    : null;
  const { data, error, isLoading, mutate } = useSWR<{
    message: string;
    value: Setting[];
  }>(
    listKey,
    SWRFetcher({
      method: 'POST',
      body: { entities },
    })
  );

  return {
    settings: data?.value,
    isLoading,
    error,
    mutate,
  };
}

export function useSetting<K extends SettingKey>(payload: {
  key: K;
  entities: SettingEntity[];
}) {
  const queryKey = payload.entities.length
    ? `settings/query?key=${encodeURIComponent(payload.key)}&entities=${encodeURIComponent(JSON.stringify(payload.entities))}`
    : null;
  const { data, error, isLoading, mutate } = useSWR<{
    message: string;
    value: SettingValueType[K] | null;
  } | null>(
    queryKey,
    SWRFetcher({
      method: 'POST',
      body: payload,
    })
  );

  return {
    setting: data?.value,
    isLoading,
    error,
    mutate,
  };
}
